import { sentimentQueryContext, selectedSentimentTechs, scopeSentimentEntry, assessPublicSentiment, sentimentDiscoveryExclusion } from '../lib/sentiment/publicSentimentScope';
/**
 * Public Sentiment Engine — PSE-1 orchestrator skeleton.
 *
 * The synthetic `public-sentiment` provider. Fans out per-tech queries
 * to three free-tier public sources, collects their
 * `PublicSentimentEntry[]` outputs, dedupes, aggregates into 5 BTM-tech
 * buckets, and returns a `PublicSentimentSection`. Cloned from the
 * `externalLiteratureOrchestrator` (LL5) pattern so the two synthetic
 * providers share testing + rendering ergonomics.
 *
 *  - `gdelt`           — GDELT 2.0 Doc API. Media-tone signal.
 *  - `local-minutes`   — MuniCode / Granicus / Civic-Plus council
 *    minutes scraper. Planning-commission and council debates.
 *  - `federal-dockets` — Regulations.gov / FERC eLibrary / NRC ADAMS
 *    docket activity. Frequency-of-filing + filing-posture signal.
 *
 * Architecture notes:
 *
 *  - Query-driven, NOT (lat, lng). Sentiment retrieval keys off the
 *    site context + the BTM tech set the report cares about. Today's
 *    skeleton builds a `[btmTech, locality]` Cartesian product; PSE-2
 *    will refine with per-source query DSLs.
 *
 *  - Per-tech aggregation. Producers emit entries with a
 *    `btmTech` field; `aggregatePublicSentiment` (pure helper) folds
 *    them into the 5 fixed buckets (`PUBLIC_SENTIMENT_BTM_TECHS`).
 *
 *  - Parallel dispatch via Promise.all (with per-source catch). One
 *    failing source doesn't take the others down. The result's
 *    `status` reflects the aggregate:
 *      every source succeeded     → 'ok'
 *      ≥ 1 source succeeded       → 'partial' (reason lists failures)
 *      every source failed        → 'unavailable'
 *
 *  - Cross-source dedup. Two sources can independently surface the
 *    same observation (e.g. a GDELT story + a council-minutes link
 *    that points at the same article). Dedup is by URL — PSE-2 will
 *    add semantic dedup once the source fetchers ship.
 *
 *  - In-memory request-scoped cache. The orchestrator caches per
 *    `(sourceId, btmTech, locality)` so two BTM categories at the
 *    same site don't re-fetch the same source. Persistent caching is
 *    a separate layer (Firestore via `withCache` on the underlying
 *    fetchers when PSE-2/3 lands).
 *
 *  - NEVER throws — even a coding bug inside one of the source
 *    fetchers is caught and turned into a per-source error string so
 *    the SED pipeline keeps moving. The test seam (`sources?` in
 *    `OrchestratePublicSentimentArgs`) lets callers inject mocked
 *    fetchers for unit-test pinning.
 *
 *  - PSE-2: real source fetchers now wired as the defaults — GDELT 2.0
 *    Doc API, MuniCode/Granicus/CivicPlus minutes scrapers,
 *    Regulations.gov + FERC eLibrary + NRC ADAMS dockets. The PSE-1
 *    stub exports below are retained for direct-call unit tests; the
 *    orchestrator no longer routes through them by default.
 */
import type {
  PerScopeSentiment,
  PerTechSentiment,
  PublicSentimentBtmTech,
  PublicSentimentEntry,
  PublicSentimentScope,
  PublicSentimentSection,
  PublicSentimentSourceId,
  SiteEvidenceDocument,
} from '../types/siteEvidence';
// PSE-2 — real fetchers replace the PSE-1 stubs as the orchestrator
// defaults. The stub exports are retained below for backward
// compatibility (some PSE-1 unit tests pin their behaviour directly).
import { fetchGdeltSentiment } from './dataProviders/gdeltSentiment';
import { fetchLocalMinutesSentiment } from './dataProviders/localMinutesSentiment';
import { fetchFederalDocketsSentiment, resetFederalDocketsFetchSession } from './dataProviders/federalDocketsSentiment';
import { resetPseFetchCircuits } from './dataProviders/pseFetchCircuit';
// PSR-FOLLOWUP-IIA-INGEST — 4th source: image-observation claims
// (IIA-2 / IIA-3) routed through a Gemini polarity classifier.
import { fetchImageObservationSentiment } from './dataProviders/imageObservationSentiment';
// PSR-5b/c — commercial sentiment providers, per-tenant-keyed. Only
// dispatched when `mode === 'standalone-psr'` AND the caller supplies
// the tenant's API key under `commercialKeys`. See `brandwatchSentiment.ts`
// + `talkwalkerSentiment.ts` for the per-vendor contracts.
import { fetchBrandwatchSentiment } from './dataProviders/sentiment/brandwatchSentiment';
import { fetchTalkwalkerSentiment } from './dataProviders/sentiment/talkwalkerSentiment';
// PSR-5d — soft-fail audit emit for the commercial-override event.
import { emitAuditEvent } from '../lib/auditLogClient';
import type { Tier1SentimentBundle } from '../lib/sentiment/tier1SentimentOrchestration';
import { fetchTier1SentimentSources } from '../lib/sentiment/tier1SentimentOrchestration';
import { buildCitationRefsFromFetchResults } from '../lib/sentiment/citationRefs';
import {
  raceWithAbort,
  rethrowIfAborted,
  throwIfAborted,
} from '../lib/abortSignal';

/* ── Tech bucket table ──────────────────────────────────────────────── */

/**
 * The five BTM-tech buckets the aggregator emits. Order is fixed so
 * downstream consumers (scoring axis, renderer) can index by position
 * if they want; the bucket id is also on each `PerTechSentiment` row.
 */
export const PUBLIC_SENTIMENT_BTM_TECHS: readonly PublicSentimentBtmTech[] = [
  'nuclear-smr',
  'natural-gas',
  'solar-bess',
  'wind-bess',
  'general-datacenter',
] as const;

/* ── Source-fetcher contract ───────────────────────────────────────── */

/**
 * Common shape returned by all three source fetchers. The orchestrator
 * only reads `.status` and `.entries`; per-source filter arguments
 * (PSE-2/3 will add them) are passed through opaquely.
 */
export type PublicSentimentSourceResult =
  | { status: 'ok'; entries: PublicSentimentEntry[] }
  | { status: 'unavailable'; reason: string; entries: PublicSentimentEntry[] }
  // PSR-FOLLOWUP-IIA-INGEST — 'empty' is a clean no-signal pass-through
  // for the image-observation source (no approved claims yet). The
  // orchestrator's status rollup treats 'empty' identically to 'ok' with
  // zero entries: not an error, just no contribution from this source.
  | { status: 'empty'; entries: PublicSentimentEntry[] };

export type PublicSentimentSourceFn = (
  queries: string[],
  filters?: unknown,
) => Promise<PublicSentimentSourceResult>;

/* ── Legacy PSE-1 source-fetcher stubs (still exported) ─────────────── */

/**
 * Empty-result stub for the GDELT source. PSE-2 wired
 * `fetchGdeltSentiment` (in `dataProviders/gdeltSentiment.ts`) as the
 * real fetcher and made it the orchestrator default; this stub stays
 * exported so the PSE-1 unit tests that pin the empty-result contract
 * keep passing without modification.
 */
export const fetchGdeltSentimentStub: PublicSentimentSourceFn = async (
  _queries: string[],
  _filters?: unknown,
): Promise<PublicSentimentSourceResult> => {
  return { status: 'ok', entries: [] };
};

/**
 * Empty-result stub for the local-minutes source. PSE-2 wired the real
 * MuniCode/Granicus/CivicPlus fetcher; see
 * `dataProviders/localMinutesSentiment.ts`.
 */
export const fetchLocalMinutesSentimentStub: PublicSentimentSourceFn = async (
  _queries: string[],
  _filters?: unknown,
): Promise<PublicSentimentSourceResult> => {
  return { status: 'ok', entries: [] };
};

/**
 * Empty-result stub for the federal-dockets source. PSE-2 wired the
 * real Regulations.gov + FERC + ADAMS fetcher; see
 * `dataProviders/federalDocketsSentiment.ts`.
 */
export const fetchFederalDocketsSentimentStub: PublicSentimentSourceFn = async (
  _queries: string[],
  _filters?: unknown,
): Promise<PublicSentimentSourceResult> => {
  return { status: 'ok', entries: [] };
};

/* ── Query composition ──────────────────────────────────────────────── */

/**
 * Per-BTM-tech base query seed. PSE-2 will refine with per-source DSL
 * (e.g. GDELT uses `theme:` filters; council minutes use free text).
 * Today the same seed text is shared across all sources.
 */
export const PUBLIC_SENTIMENT_BTM_QUERIES: Record<PublicSentimentBtmTech, string[]> = {
  'nuclear-smr': [
    'small modular reactor',
    'SMR siting community',
    'nuclear reactor approval',
  ],
  'natural-gas': [
    'natural gas power plant siting',
    'gas turbine community',
    'methane emissions plant',
  ],
  'solar-bess': [
    'solar farm community',
    'utility scale solar opposition',
    'battery storage siting',
  ],
  'wind-bess': [
    'wind farm community',
    'turbine setback ordinance',
    'wind project opposition',
  ],
  'general-datacenter': [
    'data center moratorium',
    'data center community opposition',
    'hyperscale data center siting',
  ],
};

/**
 * Extract a small locality seed term set from the SED. Mirrors
 * `deriveSiteSeedTerms` in `externalLiteratureOrchestrator` — state
 * + address-tail are the cheapest signals available without a
 * dedicated geocoder.
 */
export function deriveLocalitySeedTerms(
  sed: SiteEvidenceDocument | null | undefined,
): string[] {
  if (!sed) return [];
  const terms: string[] = [];
  const state = sed.regulatory?.data?.state;
  if (state) terms.push(state);
  // Address-tail (city / county / country) — cheapest geographic signal
  // without a dedicated reverse-geocoder. We take the head chunk too
  // since for US sites the city is usually the first comma-separated
  // segment ("Macon, GA, USA" → ["Macon", "GA", "USA"]).
  const address = sed.location?.address;
  if (address) {
    const parts = address.split(',').map(s => s.trim()).filter(s => s.length > 0 && s.length < 40);
    // Take up to two head parts (city, optionally county) — the state
    // term is already in `terms` from regulatory.data.state.
    for (const p of parts.slice(0, 2)) {
      if (!terms.includes(p)) terms.push(p);
    }
  }
  return terms;
}

/**
 * Compose final query strings for one BTM tech. Each base query is
 * emitted twice (once bare, once suffixed with the locality terms)
 * so the upstream sources have both a broad and narrow shot. Identical
 * queries are deduped.
 */
export function composePublicSentimentQueries(
  btmTech: PublicSentimentBtmTech,
  localityTerms: string[],
): string[] {
  const seeds = PUBLIC_SENTIMENT_BTM_QUERIES[btmTech] ?? [];
  const suffix = localityTerms.join(' ').trim();
  const out = new Set<string>();
  for (const q of seeds) {
    out.add(q);
    if (suffix.length > 0) out.add(`${q} ${suffix}`);
  }
  return [...out];
}

/* ── Three-layer geographic scoping (site / state / country) ─────────── */

/**
 * USPS abbreviation → full state name. Normalises `regulatory.data.state`
 * (which may arrive as either form) and lets the state layer query BOTH
 * forms — national media writes "Louisiana"; dockets often use "LA".
 */
const US_STATES: Readonly<Record<string, string>> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
const US_STATE_NAME_TO_CODE: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(US_STATES).map(([code, name]) => [name.toLowerCase(), code]),
);

/** Normalise a raw state string (abbrev OR full name) to both forms. */
export function normalizeUsState(
  raw: string | null | undefined,
): { code: string | null; name: string | null } {
  const t = (raw ?? '').trim();
  if (!t) return { code: null, name: null };
  const upper = t.toUpperCase();
  if (US_STATES[upper]) return { code: upper, name: US_STATES[upper]! };
  const code = US_STATE_NAME_TO_CODE[t.toLowerCase()];
  if (code) return { code, name: US_STATES[code]! };
  // Unknown (non-US / malformed) — keep the raw term as the name so a
  // state-layer query is still attempted; no abbreviation is invented.
  return { code: null, name: t };
}

/** Plus-Code fragment (e.g. "9Q32+8G") — a geocode, never a place name. */
const PLUS_CODE_RE = /^[23456789CFGHJMPQRVWX]{2,}\+[23456789CFGHJMPQRVWX]{2,}$/i;
/** Leading Plus-Code token + whitespace. Google addresses prepend the code
 *  to the place name in ONE comma-segment ("9Q32+8G Moss Bluff" → strip to
 *  "Moss Bluff"); this peels the geocode off the front. */
const PLUS_CODE_PREFIX_RE = /^[23456789CFGHJMPQRVWX]{2,}\+[23456789CFGHJMPQRVWX]{2,}\s+/i;

/**
 * Per-geographic-layer seed terms derived from the SED. Appended as query
 * suffixes by `composeScopedQueries`:
 *   - `site`    — city / town / county from the address head.
 *   - `state`   — full state name + USPS abbreviation.
 *   - `country` — nation (US default); the country layer ALSO carries the
 *     bare national seed so it is never empty for a quiet site.
 * `stateCode` threads to the source fetchers as the ADAMS / MuniCode state
 * filter on the state-layer pass.
 */
export interface ScopedSeedTerms {
  site: string[];
  state: string[];
  country: string[];
  stateCode: string | null;
}

export function deriveScopedSeedTerms(
  sed: SiteEvidenceDocument | null | undefined,
): ScopedSeedTerms {
  const country = ['United States'];
  if (!sed) return { site: [], state: [], country, stateCode: null };
  const { code, name } = normalizeUsState(sed.regulatory?.data?.state);
  // Site layer — city / county from the address head, excluding the state
  // token, the country token, and Plus-Code fragments.
  const site: string[] = [];
  const address = sed.location?.address;
  if (address) {
    const parts = address.split(',').map(s => s.trim()).filter(s => s.length > 0 && s.length < 48);
    for (const rawPart of parts) {
      // Peel a leading Plus-Code token off the segment ("9Q32+8G Moss Bluff"
      // → "Moss Bluff"); a segment that is ONLY a Plus-Code collapses to ''.
      const p = rawPart.replace(PLUS_CODE_PREFIX_RE, '').trim();
      if (!p) continue;
      const up = p.toUpperCase();
      if (code && up === code) continue;
      if (up === 'USA' || up === 'US' || up === 'UNITED STATES') continue;
      if (name && p.toLowerCase() === name.toLowerCase()) continue;
      if (PLUS_CODE_RE.test(p)) continue; // a bare Plus-Code segment
      if (!site.includes(p)) site.push(p);
      if (site.length >= 2) break; // city + (optionally) county
    }
  }
  const state: string[] = [];
  if (name) state.push(name);
  if (code && code !== name) state.push(code);
  return { site, state, country, stateCode: code };
}

/**
 * Compose query strings for ONE tech at ONE geographic layer. Each tech
 * seed is suffixed with every layer term (the state layer issues both
 * "<seed> Louisiana" and "<seed> LA"). The bare seed is emitted ONLY for
 * the country layer — keeping site/state strictly local so their
 * aggregates aren't polluted with national hits. Returns [] when a layer
 * has no terms and the bare seed is suppressed → that layer is skipped.
 */
export function composeScopedQueries(
  btmTech: PublicSentimentBtmTech,
  terms: readonly string[],
  includeBare: boolean,
): string[] {
  const seeds = PUBLIC_SENTIMENT_BTM_QUERIES[btmTech] ?? [];
  const out = new Set<string>();
  for (const seed of seeds) {
    if (includeBare) out.add(seed);
    for (const term of terms) {
      const tt = term.trim();
      if (tt.length > 0) out.add(`${seed} ${tt}`);
    }
  }
  return [...out];
}

/** The three geographic layers, in nesting order. */
export const PUBLIC_SENTIMENT_SCOPES: readonly PublicSentimentScope[] = ['site', 'state', 'country'] as const;

/**
 * Which layers each source is queried at. Council minutes have no national
 * analogue; federal dockets have no sub-state analogue; GDELT media + the
 * commercial firehoses span all three. A source is dispatched for a layer
 * only when that layer ALSO has a non-empty query list (see the dispatch
 * loop), so a quiet site collapses to the country layer alone.
 */
const SOURCE_SCOPES: Partial<Record<PublicSentimentSourceId, readonly PublicSentimentScope[]>> = {
  'gdelt': ['site', 'state', 'country'],
  'local-minutes': ['site', 'state'],
  'federal-dockets': ['state', 'country'],
  'image-observation': ['site'],
  'uploaded-document': ['site'],
  'brandwatch': ['site', 'state', 'country'],
  'talkwalker': ['site', 'state', 'country'],
};
function scopesForSource(sourceId: PublicSentimentSourceId): readonly PublicSentimentScope[] {
  return SOURCE_SCOPES[sourceId] ?? PUBLIC_SENTIMENT_SCOPES;
}

/**
 * Per-geographic-scope aggregator. Folds entries into the three layers by
 * their `scope` stamp (undefined → 'site' for legacy entries). Same
 * confidence-weighted mean as `aggregatePublicSentiment`. Always returns
 * all three layers so the renderer never guards on a key.
 */
export function aggregateByScope(
  entries: readonly PublicSentimentEntry[],
): PerScopeSentiment[] {
  const byScope: Record<PublicSentimentScope, { sumWeighted: number; sumConf: number; count: number }> = {
    site:    { sumWeighted: 0, sumConf: 0, count: 0 },
    state:   { sumWeighted: 0, sumConf: 0, count: 0 },
    country: { sumWeighted: 0, sumConf: 0, count: 0 },
  };
  for (const e of entries) {
    const scope = e.scope ?? 'site';
    const bucket = byScope[scope];
    if (!bucket) continue;
    const rawScore = Number.isFinite(e.sentimentScore) ? e.sentimentScore : 0;
    const score = Math.max(-1, Math.min(1, rawScore));
    const conf = Number.isFinite(e.confidence) && e.confidence > 0 ? Math.min(1, e.confidence) : 0;
    bucket.sumWeighted += score * conf;
    bucket.sumConf += conf;
    bucket.count += 1;
  }
  return PUBLIC_SENTIMENT_SCOPES.map(scope => {
    const b = byScope[scope];
    if (b.count === 0 || b.sumConf === 0) {
      return { scope, count: b.count, meanSentiment: 0, meanConfidence: 0 };
    }
    return { scope, count: b.count, meanSentiment: b.sumWeighted / b.sumConf, meanConfidence: b.sumConf / b.count };
  });
}

/* ── Per-tech aggregator (pure helper) ──────────────────────────────── */

/**
 * Pure aggregator — folds a flat `PublicSentimentEntry[]` into the 5
 * fixed BTM-tech buckets. Confidence-weighted mean to give a single
 * high-confidence observation more pull than three low-confidence
 * ones.
 *
 *  mean(score) = Σ (score_i * conf_i) / Σ conf_i
 *  mean(conf)  = Σ conf_i / count
 *
 * Returns 5 entries always — buckets with zero observations get
 * `count: 0, meanSentiment: 0, meanConfidence: 0` so the scoring
 * axis + renderer never have to guard on missing keys.
 *
 * Defensive bounds:
 *  - Non-finite scores are clamped to 0.
 *  - Non-finite or negative confidence is clamped to 0.
 *  - sentimentScore is clamped to [−1, +1] before averaging so a
 *    misbehaving source can't poison the mean.
 */
export function aggregatePublicSentiment(
  entries: readonly PublicSentimentEntry[],
): PerTechSentiment[] {
  const byTech: Record<PublicSentimentBtmTech, { sumWeighted: number; sumConf: number; count: number }> = {
    'nuclear-smr':        { sumWeighted: 0, sumConf: 0, count: 0 },
    'natural-gas':        { sumWeighted: 0, sumConf: 0, count: 0 },
    'solar-bess':         { sumWeighted: 0, sumConf: 0, count: 0 },
    'wind-bess':          { sumWeighted: 0, sumConf: 0, count: 0 },
    'general-datacenter': { sumWeighted: 0, sumConf: 0, count: 0 },
  };

  for (const e of entries) {
    const bucket = byTech[e.btmTech];
    if (!bucket) continue; // Unknown tech → silently skip (forward-compat).
    const rawScore = Number.isFinite(e.sentimentScore) ? e.sentimentScore : 0;
    const score = Math.max(-1, Math.min(1, rawScore));
    const conf = Number.isFinite(e.confidence) && e.confidence > 0 ? Math.min(1, e.confidence) : 0;
    bucket.sumWeighted += score * conf;
    bucket.sumConf += conf;
    bucket.count += 1;
  }

  return PUBLIC_SENTIMENT_BTM_TECHS.map(tech => {
    const b = byTech[tech];
    if (b.count === 0 || b.sumConf === 0) {
      return { btmTech: tech, count: b.count, meanSentiment: 0, meanConfidence: 0 };
    }
    return {
      btmTech: tech,
      count: b.count,
      meanSentiment: b.sumWeighted / b.sumConf,
      meanConfidence: b.sumConf / b.count,
    };
  });
}

/* ── Public entry point ─────────────────────────────────────────────── */

/** Post-dedup entry cap when a caller passes no `maxEntries` (the browser
 *  top-up path). Exported so scan-depth checks can name the shallowest cap a
 *  non-report-grade scan may have been collected under. */
export const DEFAULT_PUBLIC_SENTIMENT_MAX_ENTRIES = 60;

/** PSR-FOLLOWUP-IIA-INGEST — per-source cap reuses the PSR-1/PSR-2
 *  canonical constants declared further down (`PER_SOURCE_CAP_FEASIBILITY`
 *  + `PER_SOURCE_CAP_STANDALONE_PSR`). No separate constants needed. */

const DEFAULT_PUBLIC_SENTIMENT_SOURCES_BASE: PublicSentimentSourceId[] = [
  'gdelt',
  'local-minutes',
  'federal-dockets',
];

/**
 * PSR-5d — Per-tenant commercial-source key bundle. When `mode ===
 * 'standalone-psr'` AND the caller supplies a key + projectId for either
 * vendor AND the budget gate (`*BudgetRemaining`) is > 0, the orchestrator
 * adds that commercial source to the fan-out. Free sources always run.
 *
 * Per R5 — the orchestrator NEVER passes the raw siteId to the vendor;
 * `siteIdHash` is the opaque 8-char hash the orchestrator threads through
 * to the provider as `queryTag` for audit-trail purposes.
 *
 * NEVER stored — this struct lives entirely in memory for the lifetime
 * of one orchestrator call. Callers compose it from the Firestore
 * `tenants/{tenantId}/integrations` read.
 */
export interface CommercialSentimentKeys {
  brandwatch?: {
    apiKey: string;
    projectId: string;
    /** Remaining queries today (computed by the caller from the daily
     *  budget Firestore counter). 0 → orchestrator skips the source. */
    budgetRemaining: number;
  };
  talkwalker?: {
    apiKey: string;
    projectId: string;
    budgetRemaining: number;
  };
  /** Opaque siteId hash (e.g. first 8 chars of SHA-256(siteId)) for
   *  vendor audit trail. R5 — NEVER the raw site address. */
  siteIdHash?: string;
}

/** PSR-FOLLOWUP-IIA-INGEST — the 4th source only fires when the caller
 *  supplies a `siteId` (it reads `siteContext.claims[]` from Firestore).
 *  Tests + the sedBootstrap callsite both pass siteId; legacy callers
 *  without one get the 3-source behaviour unchanged. */
export interface OrchestratePublicSentimentArgs {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  /** Bypass browser cache/context reads for canonical server collection. */
  explicitContext?: boolean;
  skipCache?: boolean;
  trendWindowDays?: number;
  sourceOptions?: { includeTribalRecords?: boolean };
  sed: SiteEvidenceDocument | null;
  /** Which BTM techs to query. Defaults to all five. */
  btmTechs?: readonly PublicSentimentBtmTech[];
  /** Override the max total entries returned (post-dedup). Defaults to 60. */
  maxEntries?: number;
  /** PSR-FOLLOWUP-IIA-INGEST — site id for the image-observation source.
   *  When omitted, the image-observation source is not dispatched (the
   *  3 web sources still run). */
  siteId?: string;
  /** PSR-FOLLOWUP-IIA-INGEST — caller mode. Affects the per-source cap
   *  applied to image-observation entries:
   *    - `'sed-build'` (default) → 50 entries
   *    - `'standalone-psr'`       → 200 entries
   *  Mirrors the PSE-1 pattern for per-source caps. */
  mode?: 'sed-build' | 'standalone-psr';
  /** PSR-5d — per-tenant commercial-source keys + budget gate. Only
   *  honoured when `mode === 'standalone-psr'`. */
  commercialKeys?: CommercialSentimentKeys;
  /** PSR Phase 2 — pre-fetched tier-1 bundle (skip internal fetch when supplied). */
  tier1Bundle?: Tier1SentimentBundle;
  /** PSR Phase 2 — when true (default), fetch tier-1 sources before fan-out. */
  fetchTier1?: boolean;
  /** Test seam — inject mock source fetchers. The real fetchers
   *  structurally satisfy `PublicSentimentSourceFn`. */
  sources?: {
    gdelt?: PublicSentimentSourceFn;
    'local-minutes'?: PublicSentimentSourceFn;
    'federal-dockets'?: PublicSentimentSourceFn;
    /** PSR-FOLLOWUP-IIA-INGEST — image-observation source test seam.
     *  Takes the same `(queries) => Promise<PublicSentimentSourceResult>`
     *  shape as the other three; the production wrapper closes over
     *  `siteId` so the orchestrator's dispatch is uniform. */
    'image-observation'?: PublicSentimentSourceFn;
    /** Current source-bound uploaded consultation records, supplied by the owner collector. */
    'uploaded-document'?: PublicSentimentSourceFn;
    /** PSR-5d — Brandwatch + Talkwalker test seams. The production
     *  wrappers close over `commercialKeys` so the orchestrator's
     *  dispatch is uniform. */
    brandwatch?: PublicSentimentSourceFn;
    talkwalker?: PublicSentimentSourceFn;
  };
}

/**
 * Build the `publicSentiment` SED section for `sed`.
 *
 * Resilience model:
 *  - Each source invocation is independent — one timeout doesn't
 *    cascade.
 *  - Status semantics: ok / partial / unavailable per the LLW
 *    convention.
 *  - Empty after-dedup → 'unavailable' with reason 'no entries
 *    returned'.
 *  - The function NEVER throws — a thrown error inside a source
 *    becomes a per-source error string in the aggregated reason.
 *
 * Cache + dedup:
 *  - In-memory `Map` keyed by `${sourceId}|${tech}` so two distinct
 *    BTM techs that share queries don't double-fetch. This is the
 *    request-scoped cache; persistent caching lives in PSE-2/3.
 *  - Cross-source dedup is by URL — two sources surfacing the same
 *    article collapse to one entry. Score / confidence from the
 *    first-seen entry wins (the LLW pattern).
 */
export async function orchestratePublicSentiment(
  args: OrchestratePublicSentimentArgs,
): Promise<PublicSentimentSection> {
  throwIfAborted(args.signal);
  resetFederalDocketsFetchSession();
  resetPseFetchCircuits();
  const { sed } = args;
  const fetchedAt = new Date().toISOString();
  const seedTerms = deriveScopedSeedTerms(sed);
  const queryContext = sentimentQueryContext(sed, { mode: args.mode, windowDays: args.trendWindowDays, sourceOptions: args.sourceOptions, techs: args.btmTechs, localityTerms: seedTerms.site, stateTerms: seedTerms.state });

  // PSR Phase 2 — tier-1 deep fetchers (cache → fetch → aggregate metadata).
  const tier1Mode = args.mode === 'standalone-psr' ? 'standalone-psr' : 'feasibility';
  let tier1Bundle = args.tier1Bundle;
  if (!tier1Bundle && args.fetchTier1 !== false && args.siteId) {
    try {
      tier1Bundle = await fetchTier1SentimentSources({
        siteId: args.siteId,
        opts: {
          mode: tier1Mode,
          lat: sed?.location?.lat,
          lng: sed?.location?.lng,
          state: sed?.regulatory?.data?.state,
          locality: seedTerms.site[0],
          explicitContext: args.explicitContext, skipCache: args.skipCache,
          queryFingerprint: queryContext.fingerprint, trendWindowDays: queryContext.windowDays,
          technologyTerms: queryContext.techs.map(tech => ({ 'general-datacenter': 'data center', 'natural-gas': 'gas turbine', 'nuclear-smr': 'nuclear reactor', 'solar-bess': 'solar battery storage', 'wind-bess': 'wind farm' })[tech]),
          signal: args.signal,
        },
      });
      throwIfAborted(args.signal);
    } catch (error) {
      rethrowIfAborted(args.signal, error);
      tier1Bundle = undefined;
    }
  }

  const tier1Facts = tier1Bundle?.results.flatMap(r =>
    r.normalizedFacts.map(f => ({
      sourceId: r.sourceId,
      key: f.key,
      label: f.label,
      value: f.value,
      units: f.units,
      url: f.url,
      observedAt: f.observedAt,
    })),
  );
  const tier1CitationRefs = (tier1Bundle?.citationRefs?.length
    ? tier1Bundle.citationRefs
    : buildCitationRefsFromFetchResults(tier1Bundle?.results ?? [])
  ).map(c => ({
    index: c.index,
    label: c.label,
    url: c.url,
    sourceId: c.sourceId,
    factKey: c.factKey,
  }));
  const tier1SourcesAttempted = tier1Bundle?.sourcesAttempted;

  const attachTier1 = (section: PublicSentimentSection): PublicSentimentSection => ({
    ...section,
    queryContext,
    assessment: assessPublicSentiment(section.entries),
    sourceCoverage: (tier1Bundle?.results ?? []).map(result => ({ sourceId: result.sourceId, coverage: result.coverage, errors: result.errors })),
    ...(tier1Facts?.length ? { tier1Facts } : {}),
    ...(tier1CitationRefs?.length ? { tier1CitationRefs } : {}),
    ...(tier1SourcesAttempted?.length ? { tier1SourcesAttempted } : {}),
  });

  const techs = args.btmTechs ?? selectedSentimentTechs(sed);
  // PSR-FOLLOWUP-IIA-INGEST — only attempt the image-observation
  // source when the caller supplied a `siteId` OR explicitly injected
  // a mock for it. Otherwise legacy 3-source behaviour is preserved.
  const imageObservationEnabled = !!args.siteId || !!args.sources?.['image-observation'];
  // PSR-5d — commercial sources only fire in `standalone-psr` mode AND
  // when the tenant has provisioned a key AND the daily budget is not
  // exhausted. The budget check uses `Number.isFinite()` to guard
  // against a misconfigured Firestore counter (NaN / Infinity → skip).
  const standalonePsrMode = args.mode === 'standalone-psr';
  const brandwatchEnabled = standalonePsrMode && (
    !!args.sources?.brandwatch || (
      !!args.commercialKeys?.brandwatch?.apiKey
      && !!args.commercialKeys.brandwatch.projectId
      && Number.isFinite(args.commercialKeys.brandwatch.budgetRemaining)
      && args.commercialKeys.brandwatch.budgetRemaining > 0
    )
  );
  const talkwalkerEnabled = standalonePsrMode && (
    !!args.sources?.talkwalker || (
      !!args.commercialKeys?.talkwalker?.apiKey
      && !!args.commercialKeys.talkwalker.projectId
      && Number.isFinite(args.commercialKeys.talkwalker.budgetRemaining)
      && args.commercialKeys.talkwalker.budgetRemaining > 0
    )
  );
  const sourcesAttempted: PublicSentimentSourceId[] = imageObservationEnabled
    ? [...DEFAULT_PUBLIC_SENTIMENT_SOURCES_BASE, 'image-observation']
    : [...DEFAULT_PUBLIC_SENTIMENT_SOURCES_BASE];
  if (args.sources?.['uploaded-document']) sourcesAttempted.push('uploaded-document');
  if (brandwatchEnabled) sourcesAttempted.push('brandwatch');
  if (talkwalkerEnabled) sourcesAttempted.push('talkwalker');
  // PSR-FOLLOWUP-IIA-INGEST — per-source cap. Applied AFTER each
  // source's entries arrive but BEFORE the cross-source dedup +
  // aggregate. Keeps a single chatty source from monopolising the
  // 60-entry post-dedup ceiling.
  // PSR-FOLLOWUP-IIA-INGEST initially typed `mode: 'sed-build' | 'standalone-psr'`
  // while PSR-2's canonical union is `'feasibility' | 'standalone-psr'`. The two
  // values are semantically identical for the cap (`sed-build` ≡ `feasibility`
  // = the SED bootstrap context). Normalise to the canonical vocabulary
  // before calling the cap helper so a single helper owns the table.
  const normalisedMode: PublicSentimentMode | undefined =
    args.mode === 'standalone-psr' ? 'standalone-psr' : 'feasibility';
  const perSourceCap = perSourceCapFor(normalisedMode);

  // PSE-2 — defaults are now the real fetchers. The fetchers carry
  // narrower per-source filter types (GdeltSentimentFilters,
  // LocalMinutesFilters, FederalDocketsFilters) than the shared
  // `PublicSentimentSourceFn` advertises (filters: unknown). The TS cast
  // is needed because narrower parameters are not assignable to wider
  // parameters under contravariance. The dispatcher NOW passes `{ state }`
  // as the 2nd arg on the state layer (LocalMinutes/ADAMS consume it; GDELT
  // ignores the unknown key) — see `dispatchSourceTech`. Mirrors the LL5
  // pattern (`fetchCrossref as unknown as LiteratureProviderFn`). Test seam
  // still wins when callers inject mocks via `args.sources`.
  const gdelt = args.sources?.gdelt ?? (fetchGdeltSentiment as unknown as PublicSentimentSourceFn);
  const localMinutes = args.sources?.['local-minutes'] ?? (fetchLocalMinutesSentiment as unknown as PublicSentimentSourceFn);
  const federalDockets = args.sources?.['federal-dockets'] ?? (fetchFederalDocketsSentiment as unknown as PublicSentimentSourceFn);
  // PSR-FOLLOWUP-IIA-INGEST — production fetcher takes (siteId), not
  // (queries). Wrap it in a closure so the orchestrator's dispatcher
  // can call it uniformly. Tests inject a `PublicSentimentSourceFn`
  // directly via `args.sources['image-observation']`.
  const imageObservationDefault: PublicSentimentSourceFn = async (_queries, _filters) => {
    // The image-observation source is per-site, not per-tech-query.
    // The orchestrator's caching key is `(sourceId, tech)`, so this
    // closure is called once per tech — we return the SAME result on
    // every invocation (the cache short-circuits subsequent calls
    // anyway, but the determinism is what matters here). The actual
    // BTM-tech bucketing happens INSIDE the entries via
    // `classifyBtmTech`; the aggregator then routes them.
    if (!args.siteId) {
      return { status: 'unavailable', reason: 'siteId required for image-observation source', entries: [] };
    }
    const r = await fetchImageObservationSentiment(args.siteId, {
      signal: args.signal,
    });
    // Coerce the 'empty' variant to 'ok' for the orchestrator's
    // status rollup (the orchestrator's cache type is the 3-state
    // union; 'empty' is semantically a clean pass-through).
    if (r.status === 'empty') return { status: 'ok', entries: r.entries };
    return r;
  };
  const imageObservation = args.sources?.['image-observation'] ?? imageObservationDefault;

  // PSR-5d — commercial-source production wrappers. The orchestrator
  // dispatches every source as `(queries) => Promise<...>`; these
  // wrappers close over the per-tenant credentials (NEVER logged) so
  // the inner provider signature is uniform with the four free sources.
  const brandwatchDefault: PublicSentimentSourceFn = async (queries) => {
    const k = args.commercialKeys?.brandwatch;
    if (!k) {
      return { status: 'unavailable', reason: 'brandwatch: no commercial key', entries: [] };
    }
    const filters: {
      apiKey: string;
      projectId: string;
      queryTag?: string;
      signal?: AbortSignal;
    } = {
      apiKey: k.apiKey,
      projectId: k.projectId,
    };
    if (args.commercialKeys?.siteIdHash) {
      filters.queryTag = args.commercialKeys.siteIdHash;
    }
    if (args.signal) filters.signal = args.signal;
    return fetchBrandwatchSentiment(queries, filters);
  };
  const talkwalkerDefault: PublicSentimentSourceFn = async (queries) => {
    const k = args.commercialKeys?.talkwalker;
    if (!k) {
      return { status: 'unavailable', reason: 'talkwalker: no commercial key', entries: [] };
    }
    const filters: {
      apiKey: string;
      projectId: string;
      queryTag?: string;
      signal?: AbortSignal;
    } = {
      apiKey: k.apiKey,
      projectId: k.projectId,
    };
    if (args.commercialKeys?.siteIdHash) {
      filters.queryTag = args.commercialKeys.siteIdHash;
    }
    if (args.signal) filters.signal = args.signal;
    return fetchTalkwalkerSentiment(queries, filters);
  };
  const brandwatch = args.sources?.brandwatch ?? brandwatchDefault;
  const talkwalker = args.sources?.talkwalker ?? talkwalkerDefault;

  const scopedTerms = deriveScopedSeedTerms(sed);

  // Compose the full per-tech × per-scope query set up front so it's
  // deterministic + auditable on the section payload. Site/state layers
  // are suffixed-only (kept strictly local so their aggregates aren't
  // polluted by national hits); the country layer also carries the bare
  // national seed so it is never empty for a quiet site.
  const queriesIssued: string[] = [];
  const queriesByTechScope = new Map<string, string[]>();
  for (const tech of techs) {
    for (const scope of PUBLIC_SENTIMENT_SCOPES) {
      const terms = scope === 'site' ? scopedTerms.site
        : scope === 'state' ? scopedTerms.state
        : scopedTerms.country;
      const qs = composeScopedQueries(tech, terms, /* includeBare */ scope === 'country');
      queriesByTechScope.set(`${tech}|${scope}`, qs);
      for (const q of qs) {
        if (!queriesIssued.includes(q)) queriesIssued.push(q);
      }
    }
  }

  /**
   * Request-scoped cache. Key is `${sourceId}|${tech}|${scope}` — each
   * geographic layer is a distinct query set, so layers cache + fetch
   * independently. Multiple `orchestratePublicSentiment` calls do NOT
   * share this cache — that's the sedBootstrap PSE Firestore layer.
   */
  const cache = new Map<string, PublicSentimentSourceResult>();

  /**
   * Dispatch one source × one tech × one geographic layer through the
   * cache. Threads the USPS state filter on the state layer (so NRC ADAMS
   * + MuniCode narrow to the right jurisdiction), stamps every returned
   * entry with its `scope`, and catches thrown errors so the orchestrator
   * never propagates.
   */
  const dispatchSourceTech = async (
    sourceId: PublicSentimentSourceId,
    sourceFn: PublicSentimentSourceFn,
    tech: PublicSentimentBtmTech,
    scope: PublicSentimentScope,
  ): Promise<{ id: PublicSentimentSourceId; entries: PublicSentimentEntry[]; error?: string }> => {
    const cacheKey = `${sourceId}|${tech}|${scope}`;
    let result = cache.get(cacheKey);
    if (!result) {
      try {
        throwIfAborted(args.signal);
        const queries = queriesByTechScope.get(`${tech}|${scope}`) ?? [];
        // State layer carries the USPS state filter; site/country layers
        // pass none (local via the query suffix; country is national).
        const filterValues: Record<string, unknown> = { trendWindowDays: queryContext.windowDays };
        if (scope === 'state' && scopedTerms.stateCode) {
          filterValues.state = scopedTerms.stateCode;
        }
        if (args.signal) filterValues.signal = args.signal;
        const filters = Object.keys(filterValues).length > 0 ? filterValues : undefined;
        result = await raceWithAbort(sourceFn(queries, filters), args.signal);
        throwIfAborted(args.signal);
      } catch (e) {
        rethrowIfAborted(args.signal, e);
        const message = e instanceof Error ? e.message : String(e);
        result = { status: 'unavailable', reason: message, entries: [] };
      }
      cache.set(cacheKey, result);
    }
    // Stamp the geographic layer on every entry (the cache slot stays
    // unstamped; the key already partitions by scope).
    const stamped = result.entries.map(e => ({ ...e, scope: e.scope ?? scope }));
    if (result.status === 'ok' || result.status === 'empty') {
      // 'empty' (PSR-FOLLOWUP-IIA-INGEST) is a clean no-signal
      // pass-through — same handling as 'ok' with zero entries.
      return { id: sourceId, entries: stamped };
    }
    return { id: sourceId, entries: stamped, error: result.reason };
  };

  // Fan out: each source × each tech × each of the source's geographic
  // layers, in parallel. A query-driven source skips a layer with no
  // query terms (a site with no city → no site layer), so a quiet site
  // collapses to the country layer alone. The image-observation source
  // is per-site (query-independent) and always fires at its fixed layer.
  const tasks: Array<Promise<{ id: PublicSentimentSourceId; entries: PublicSentimentEntry[]; error?: string }>> = [];
  const fanOutSource = (
    sourceId: PublicSentimentSourceId,
    sourceFn: PublicSentimentSourceFn,
    opts?: { queryIndependent?: boolean },
  ): void => {
    for (const tech of techs) {
      for (const scope of scopesForSource(sourceId)) {
        const qlist = queriesByTechScope.get(`${tech}|${scope}`) ?? [];
        if (!opts?.queryIndependent && qlist.length === 0) continue;
        tasks.push(dispatchSourceTech(sourceId, sourceFn, tech, scope));
      }
    }
  };
  fanOutSource('gdelt', gdelt);
  fanOutSource('local-minutes', localMinutes);
  fanOutSource('federal-dockets', federalDockets);
  if (imageObservationEnabled) {
    fanOutSource('image-observation', imageObservation, { queryIndependent: true });
  }
  if (args.sources?.['uploaded-document']) {
    fanOutSource('uploaded-document', args.sources['uploaded-document'], { queryIndependent: true });
  }
  // PSR-5d — commercial-source fan-out, gated on standalone-psr mode +
  // key + budget. The orchestrator's per-source cap still applies (200
  // entries in standalone-psr mode); the per-tenant DAILY budget is the
  // caller's responsibility to track + decrement.
  if (brandwatchEnabled) {
    fanOutSource('brandwatch', brandwatch);
  }
  if (talkwalkerEnabled) {
    fanOutSource('talkwalker', talkwalker);
  }
  const settled = await raceWithAbort(Promise.all(tasks), args.signal);
  throwIfAborted(args.signal);

  // Aggregate + dedup by URL across all (source × tech) shards. Track
  // per-source success/failure for the status rollup. A source counts
  // as 'successful' iff it returned ok on at least one tech shard.
  //
  // PSR-FOLLOWUP-IIA-INGEST — per-source cap is applied here, BEFORE
  // the post-dedup `maxEntries` slice. We track per-source counts so a
  // single chatty source (e.g. an image-observation set with 300
  // approved flyers) can't monopolise the post-dedup bucket.
  //
  // PSR-5d — Conflict resolution on URL collision: when a commercial
  // source (brandwatch / talkwalker) and a free source (gdelt /
  // local-minutes / federal-dockets / image-observation) both emit an
  // entry with the same URL, the commercial entry WINS (it has the
  // calibrated 0.70 confidence vs the free source's typical 0.07-0.25).
  // The free entry is dropped from the aggregate, and a `psr.commercial_override`
  // audit event surfaces both polarities so reviewers can spot a bad
  // commercial classification.
  //
  // Implementation: two-pass. First pass collects commercial entries
  // (and marks URLs as commercially-owned). Second pass collects free
  // entries, dropping any URL the first pass claimed and emitting the
  // override audit. This keeps the per-source caps respected (commercial
  // cap fills first; free entries that lose the collision don't consume
  // a free-source slot since they're never appended).
  const seen = new Set<string>();
  const excludedUrls = new Set<string>();
  const discoveryExclusions: NonNullable<PublicSentimentSection['discoveryExclusions']> = [];
  const aggregated: PublicSentimentEntry[] = [];
  const errorsBySource = new Map<PublicSentimentSourceId, string[]>();
  const successesBySource = new Set<PublicSentimentSourceId>();
  const perSourceCount = new Map<PublicSentimentSourceId, number>();
  /** Sources whose entries WIN on URL collisions. Commercial > free. */
  const COMMERCIAL_SOURCES: ReadonlySet<PublicSentimentSourceId> = new Set([
    'brandwatch',
    'talkwalker',
  ]);
  /** Map of url → the entry that claimed it (so a later free-source
   *  collision can read out the commercial entry for the audit emit). */
  const commercialUrlEntries = new Map<string, PublicSentimentEntry>();

  const ingestEntries = (
    r: { id: PublicSentimentSourceId; entries: PublicSentimentEntry[]; error?: string },
  ): void => {
    if (r.error) {
      const prior = errorsBySource.get(r.id) ?? [];
      if (!prior.includes(r.error)) prior.push(r.error);
      errorsBySource.set(r.id, prior);
    } else {
      successesBySource.add(r.id);
    }
    for (const e of r.entries) {
      if (!e.url) continue;
      const exclusion = sentimentDiscoveryExclusion(e, queryContext, fetchedAt);
      if (exclusion) {
        if (!excludedUrls.has(e.url)) {
          excludedUrls.add(e.url);
          discoveryExclusions.push({ url: e.url, reason: exclusion, fetchedAt: e.fetchedAt, ...(e.observedDate ? { observedDate: e.observedDate } : {}) });
        }
        continue;
      }
      if (seen.has(e.url)) {
        // URL collision. Two sub-cases:
        //   (a) the prior winner was a commercial source AND the
        //       incoming is a free source → commercial-override emit.
        //   (b) the prior winner was anything else → no-op (first
        //       seen wins, matches the PSE-2/PSR-1 contract).
        const isCommercial = COMMERCIAL_SOURCES.has(r.id);
        const prior = commercialUrlEntries.get(e.url);
        if (!isCommercial && prior) {
          // PSR-5d audit emit — soft-fail; never blocks orchestrator.
          // Metadata carries BOTH polarities so the reviewer can spot
          // a Brandwatch mis-classification without re-fetching.
          // The url is included as the diagnostic anchor; opaque
          // siteId hash is included when available (R5).
          void emitAuditEvent({
            action: 'psr.commercial_override',
            target: { type: 'other', id: e.url, label: 'psr-url-collision' },
            metadata: {
              commercialSource: prior.sourceId,
              commercialPolarity: prior.sentimentScore,
              commercialConfidence: prior.confidence,
              freeSource: r.id,
              freePolarity: e.sentimentScore,
              freeConfidence: e.confidence,
              siteIdHash: args.commercialKeys?.siteIdHash,
            },
          });
        }
        continue;
      }
      const priorCount = perSourceCount.get(r.id) ?? 0;
      if (priorCount >= perSourceCap) continue;
      perSourceCount.set(r.id, priorCount + 1);
      seen.add(e.url);
      aggregated.push(e);
      if (COMMERCIAL_SOURCES.has(r.id)) {
        commercialUrlEntries.set(e.url, e);
      }
    }
  };

  // Pass 1 — commercial sources claim URLs first.
  for (const r of settled) {
    throwIfAborted(args.signal);
    if (COMMERCIAL_SOURCES.has(r.id)) ingestEntries(r);
  }
  // Pass 2 — free sources. URLs already claimed trigger the override emit.
  for (const r of settled) {
    throwIfAborted(args.signal);
    if (!COMMERCIAL_SOURCES.has(r.id)) ingestEntries(r);
  }

  const maxEntries = args.maxEntries ?? DEFAULT_PUBLIC_SENTIMENT_MAX_ENTRIES;
  // Interleave locality buckets so broad national coverage cannot consume the entire cap.
  const scoped = aggregated.map(entry => scopeSentimentEntry(entry, queryContext, fetchedAt));
  const buckets = (['site', 'state', 'country'] as const).map(scope => scoped.filter(entry => entry.scope === scope));
  const capped: PublicSentimentEntry[] = [];
  for (let i = 0; capped.length < maxEntries && buckets.some(bucket => i < bucket.length); i++) {
    for (const bucket of buckets) if (bucket[i] && capped.length < maxEntries) capped.push(bucket[i]!);
  }
  const perTech = aggregatePublicSentiment(capped);
  const perScope = aggregateByScope(capped);

  // Reason string aggregates per-source failures (one line per source
  // that failed across all its tech shards).
  const reasonParts: string[] = [];
  for (const [sourceId, msgs] of errorsBySource.entries()) {
    // Only report sources that failed on EVERY tech shard. A source
    // that succeeded on some techs and failed on others is partial
    // success; the section-level status captures that, and stuffing
    // per-shard errors into `reason` adds noise without signal.
    if (!successesBySource.has(sourceId)) {
      reasonParts.push(`${sourceId}: ${msgs.join('; ')}`);
    }
  }

  // Status rollup. A source is "fully successful" only if it never
  // produced an error across its tech shards.
  const fullSuccesses = sourcesAttempted.filter(s => successesBySource.has(s) && !errorsBySource.has(s));

  if (capped.length === 0 && fullSuccesses.length === 0) {
    // Hard unavailable — every source produced an error and we have
    // nothing to show. Reason carries the failure list.
    return attachTier1({
      status: 'unavailable',
      discoveryExclusions,
      entries: [],
      perTech,
      perScope,
      queriesIssued,
      sourcesAttempted,
      fetchedAt,
      reason: reasonParts.length > 0
        ? `all sources failed: ${reasonParts.join('; ')}`
        : 'no entries returned',
    });
  }

  // 'ok' iff every attempted source was fully successful AND we have
  // at least one entry. Anything else (partial source failure OR all
  // sources succeeded but zero entries) is 'partial'.
  const status: PublicSentimentSection['status'] =
    fullSuccesses.length === sourcesAttempted.length && capped.length > 0
      ? 'ok'
      : 'partial';

  return attachTier1({
    status,
    discoveryExclusions,
    entries: capped,
    perTech,
    perScope,
    queriesIssued,
    sourcesAttempted,
    fetchedAt,
    reason: reasonParts.length > 0
      ? reasonParts.join('; ')
      : (capped.length === 0 ? 'no entries returned' : undefined),
  });
}

/* ── PSR-1 — Standalone Public Sentiment Report mode wrapper ───────────
 *
 * The PSR (Public Sentiment Report) reuses the canonical PSE-1+2
 * orchestrator above (`orchestratePublicSentiment`) for the actual
 * provider fan-out, but wraps it with a `mode` switch + per-source caps
 * to differentiate embedded use (inside a feasibility / environmental /
 * etc. report) from standalone PSR use (a dedicated deliverable that
 * warrants a deeper sweep).
 *
 * Hard rules honoured:
 *   - Single primitive — PSR-1 does NOT clone the orchestrator. PSR-2
 *     will route `runPublicSentimentOrchestrator` through the existing
 *     `orchestratePublicSentiment` primitive with the cap baked into
 *     each provider's pagination boundary.
 *   - `Number.isFinite()` guards on every numeric input.
 *   - No Firestore writes — callers persist via `stripUndefined()`.
 *
 * For PSR-1 the wrapper returns a structurally-valid empty payload
 * (status: 'unavailable', empty entries) which downstream code treats as
 * a graceful "no public record observed in the trend window". PSR-2
 * wires the real provider fan-out + SENTIMENT INDEX 0-100 composer.
 */

/**
 * Mode of operation — caller-supplied so per-source caps + downstream
 * sweep depth can vary without forking the primitive.
 *
 * Default = `'feasibility'`. When omitted, the orchestrator behaves as
 * if the caller is the SED compiler (embedded use).
 */
export type PublicSentimentMode = 'feasibility' | 'standalone-psr';

/**
 * Per-source cap. Embedded use (`'feasibility'`) caps at 50 entries to
 * bound cost + latency; standalone PSR lifts to 200 because the user
 * commissioned a dedicated report and expects deeper coverage.
 *
 * The cap is a SOFT guideline for providers — a single provider may
 * return fewer entries even when more exist. PSR-2 will wire the cap
 * into each provider's pagination boundary.
 */
export const PER_SOURCE_CAP_FEASIBILITY = 50;
export const PER_SOURCE_CAP_STANDALONE_PSR = 200;

/**
 * Compute the per-source entry cap for a given mode. Exported so the
 * provider fan-out (when PSR-2 wires it) reads the same constant the
 * orchestrator does.
 */
export function perSourceCapFor(mode: PublicSentimentMode | undefined): number {
  switch (mode) {
    case 'standalone-psr':
      return PER_SOURCE_CAP_STANDALONE_PSR;
    case 'feasibility':
    default:
      // The default branch covers `undefined` (legacy callers that
      // haven't yet threaded `mode`) so behaviour is back-compat.
      return PER_SOURCE_CAP_FEASIBILITY;
  }
}

/**
 * Caller-supplied options. All fields optional — the orchestrator falls
 * through to sensible defaults when omitted. PSR-2 will extend this with
 * the actual provider override hooks.
 */
export interface PublicSentimentOptions {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  /**
   * Mode of operation — `'feasibility'` (embedded use inside a SED
   * compile) or `'standalone-psr'` (the PSR runs as its own deliverable
   * and the per-source cap lifts to 200). Defaults to `'feasibility'`.
   */
  mode?: PublicSentimentMode;
  /**
   * Trend window in days. Defaults to 365. Bounded [30, 1825] at the
   * validator boundary; values outside the bound are clamped (NOT
   * rejected) so a misconfigured caller doesn't break the orchestrator.
   */
  trendWindowDays?: number;
  /**
   * Optional comparable-site cohort size. Defaults to 5 for embedded
   * use, 8 for `'standalone-psr'` (the dedicated report can afford a
   * deeper benchmark). Bounded [3, 12].
   */
  benchmarkCohortSize?: number;
  /**
   * Opt-in tribal records pull. Off by default; PSR-2 will route this
   * through the tribal-government redaction layer (separate from the
   * generic municipal redaction).
   */
  includeTribalRecords?: boolean;
  /**
   * Explicit per-source cap override. When provided and finite, takes
   * precedence over the mode-derived default. Bounded [1, 1000] — values
   * outside the bound are clamped (NOT rejected). Useful when a caller
   * wants a smaller sweep for cost reasons even in standalone-psr mode.
   */
  perSourceCap?: number;
}

/**
 * Per-entry payload for the PSR-1 standalone wrapper — a single
 * public-record observation. Distinct from PSE-1+2's `PublicSentimentEntry`
 * (in `../types/siteEvidence`) which is the SED-section entry shape. The
 * PSR wrapper has its own report-level shape with stakeholder + theme
 * classification; PSR-2 will reconcile the two during the provider fan-out.
 */
export interface PsrPublicSentimentEntry {
  /** Source provider that produced this entry. */
  source: 'gdelt' | 'municipal-minutes' | 'regulations-gov' | 'ferc' | 'adams' | 'news-api' | 'manual';
  /** Publication/event timestamp; empty when unknown or only a first-seen date is available. */
  observedAt: string;
  fetchedAt?: string;
  firstSeenDate?: string;
  scheduledDate?: string;
  confidence?: number;
  scope?: PublicSentimentScope;
  /** 1-3 sentence summary or quote. */
  text: string;
  /** Polarity in [-1, 1]. `Number.isFinite()` guarded at compute time. */
  polarity: number;
  /** Stakeholder group when known. */
  stakeholderGroup?: string;
  /** Theme label when classified. */
  theme?: string;
  /** Provider-specific URL or document id. */
  refUrl?: string;
}

/**
 * Top-level PSR-1 wrapper return shape. Mirrors the existing SED-section
 * pattern (status + entries + reason) so PSE-1+2 can land this payload
 * on the `publicSentiment` section without further reshaping.
 */
export interface PublicSentimentResult {
  /** `'ok'`: at least one provider returned entries.
   *  `'partial'`: some providers failed; the entries we have are still
   *  usable. `reason` carries which providers failed.
   *  `'unavailable'`: every provider failed OR no coverage in the
   *  trend window. `entries` is empty; `reason` describes why. */
  status: 'ok' | 'partial' | 'unavailable';
  /** Mode this run executed in — surfaced on the result so downstream
   *  callers can render the correct sweep-depth label without
   *  re-deriving it. */
  mode: PublicSentimentMode;
  /** Per-source cap applied in this run (derived from `mode` or the
   *  explicit override on options). */
  perSourceCap: number;
  /** Entries pulled (subject to per-source cap). */
  entries: PsrPublicSentimentEntry[];
  /** ISO-8601 timestamp of this run. */
  fetchedAt: string;
  /** Human-readable explanation when status != 'ok'. */
  reason?: string;
}

/**
 * Clamp a numeric input to a [min, max] band, guarding against
 * non-finite values. Returns `fallback` when the input is undefined,
 * null, NaN, Infinity, or otherwise non-finite.
 */
function clampFinite(
  v: number | undefined | null,
  min: number,
  max: number,
  fallback: number,
): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

/**
 * Run the public sentiment orchestrator — PSR-1 standalone wrapper.
 * PSR-1 scaffold returns an empty `'unavailable'` payload because the
 * provider fan-out is deferred to PSR-2. The function signature is the
 * long-term API surface — callers wire against this now and the only
 * thing that changes when PSR-2 lands is the internal provider
 * implementation (which will route through `orchestratePublicSentiment`
 * above with the per-source cap baked into provider pagination).
 *
 * Inputs:
 *   - `siteId` — the site whose sentiment we're sweeping. Not yet
 *     consumed by the scaffold body, but kept on the signature so
 *     PSR-2's provider fan-out (GDELT geo-fence, MuniCode jurisdiction
 *     lookup, etc.) has the handle.
 *   - `options` — see PublicSentimentOptions above.
 */
export async function runPublicSentimentOrchestrator(
  siteId: string,
  options?: PublicSentimentOptions,
): Promise<PublicSentimentResult> {
  const { runPublicSentimentStandalone } = await import('./publicSentimentStandalone');
  const standalone = await runPublicSentimentStandalone(siteId, options);
  return {
    status: standalone.status,
    mode: standalone.mode,
    perSourceCap: standalone.perSourceCap,
    entries: standalone.entries,
    fetchedAt: standalone.fetchedAt,
    reason: standalone.reason,
  };
}
